import { createContext } from '@direct-frontend/modules-context';

/**
 * Опции конфигурации для поведения мемоизации.
 *
 * @typeParam T - Тип закешированного значения
 * @typeParam P - Тип параметра функции мемоизации
 */
interface MemoizationOptions<T, P> {
    /**
     * Пользовательская функция равенства для сравнения параметров функции.
     * Когда предоставлена, эта функция определяет, должны ли два значения параметров считаться равными,
     * позволяя попадания в кеш для семантически эквивалентных, но не строго равных параметров.
     *
     * @param a - Первое значение параметра для сравнения
     * @param b - Второе значение параметра для сравнения
     * @returns `true`, если параметры считаются равными, `false` в противном случае
     *
     * @example
     * ```ts
     * const options = {
     *   equals: (a, b) => a.id === b.id
     * };
     * ```
     */
    equals?: (a: P, b: P) => boolean;

    /**
     * Функция, которая вызывается при получении значения из кеша.
     * Вызывается каждый раз, когда мемоизированная функция возвращает значение из кеша
     * вместо повторного вычисления, что позволяет отслеживать или выполнять побочные эффекты при попаданиях в кеш.
     *
     * @param value - Закешированное значение, которое было получено
     *
     * @example
     * ```ts
     * const options = {
     *   onCacheHit: (value) => console.log('Попадание в кеш для:', value)
     * };
     * ```
     */
    onCacheHit?: (value: T) => void;
}

type Cache<K, T extends object> = Map<K, WeakRef<T>>;

/**
 * Хранилище экземпляров потоков для каждого запроса.
 * Обеспечивает контекстно-зависимое кеширование, которое изолирует кеши мемоизации между различными контекстами выполнения.
 */
const { getContext } = createContext<Map<symbol, Cache<unknown, object>>>({
    name: 'modules-flow/memoizeFlowFactory',
    getInitialValue: () => new Map(),
});

/**
 * Создает мемоизированную версию функции, которая кеширует результаты с использованием WeakRef.
 * Кеш автоматически очищается, когда объекты собираются сборщиком мусора.
 *
 * @internal Только для использования в инфраструктурном коде
 * @typeParam T - Тип возвращаемого значения функции (должен быть объектом)
 * @typeParam P - Тип параметра функции
 * @param fn - Функция для мемоизации
 * @param options - Опциональная конфигурация
 * @returns Мемоизированная версия входной функции
 *
 * @example
 * ```ts
 * const memoized = memoizeFlowFactory(() => ({ data: 'value' }));
 * const result1 = memoized();
 * const result2 = memoized(); // Возвращает кешированный результат
 * ```
 *
 * @example
 * ```ts
 * const memoized = memoizeFlowFactory(
 *   (id: number) => ({ id, data: 'value' }),
 *   { equals: (a, b) => a === b }
 * );
 * ```
 */
export function memoizeFlowFactory<T extends object, P = never>(
    fn: () => T,
    options?: MemoizationOptions<T, P>
): () => T;
export function memoizeFlowFactory<T extends object, P = never>(
    fn: (param: P) => T,
    options?: MemoizationOptions<T, P>
): (param: P) => T;
export function memoizeFlowFactory<T extends object, P = never>(
    fn: (param?: P) => T,
    options?: MemoizationOptions<T, P>
): (param?: P) => T {
    type Key = P | symbol | undefined;

    const cacheKey = Symbol();
    const notFound = Symbol();

    const registry = new FinalizationRegistry(({ cache, key }: { cache: Cache<Key, T>; key: Key }) => {
        cache.delete(key);
    });

    const findKey = (cache: Cache<Key, T>, param: Key): Key => {
        if (!options?.equals) {
            return param;
        }

        for (const [key] of cache) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- при итерации ключ гарантированно не undefined
            if (options.equals(param as P, key as P)) {
                return key;
            }
        }

        return notFound;
    };

    return (param?: Key) => {
        let value: T | undefined;

        const storage = getContext();

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- контекст не знает о типах экземпляров
        let cache = storage.get(cacheKey) as Cache<Key, T> | undefined;
        if (!cache) {
            cache = new Map();
            storage.set(cacheKey, cache);
        }

        const key = findKey(cache, param);
        const ref = cache.get(key);
        value = ref?.deref();

        if (ref && !value) {
            cache.delete(key);
        }

        if (value) {
            options?.onCacheHit?.(value);
            return value;
        }

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- параметр может быть undefined и это нормально
        value = fn(param as P);
        cache.set(param, new WeakRef(value));
        registry.register(value, { cache, key: param });

        return value;
    };
}
